import { uniq } from "lodash";
import reduceReducers from "reduce-reducers";

import { WEIGH_LOT_SCAN } from "constants/actionTypes";
import { WeighLotScanStatus } from "constants/weighScanning";

import {
  apiModelOfflineFetchReducer,
  apiModelOfflineUpdateReducer,
  apiModelOfflineCreateReducer,
  apiModelOfflineDeleteReducer,
  apiModelOfflineCommentReducer,
  bulkUpdateCases,
} from "lib/reducers";
import { deserializeWeighLotScan } from "lib/serializers/weighLotScans";

const create = apiModelOfflineCreateReducer(WEIGH_LOT_SCAN, {
  deserializer: deserializeWeighLotScan,
});

const deleteReducer = apiModelOfflineDeleteReducer(WEIGH_LOT_SCAN);

const fetch = apiModelOfflineFetchReducer(WEIGH_LOT_SCAN, {
  deserializer: deserializeWeighLotScan,
});

const update = apiModelOfflineUpdateReducer(WEIGH_LOT_SCAN, {
  deserializer: deserializeWeighLotScan,
});

const comment = apiModelOfflineCommentReducer(WEIGH_LOT_SCAN);

function bulkUpdateAndCreateReducer(state, action) {
  switch (action.type) {
    case WEIGH_LOT_SCAN.BULK_UPDATE_OR_CREATE.REQUEST: {
      return bulkUpdateCases(state, action, "byId", deserializeWeighLotScan);
    }
    // The backend runs the whole keep/ignore decision (siblings ignored, reweigh vs mapped
    // status) and returns every Weigh Lot Scan it touched - merge that in directly instead of
    // waiting on a separate changes-since fetch, the same way a plain UPDATE.SUCCESS would.
    case WEIGH_LOT_SCAN.KEEP.SUCCESS:
    case WEIGH_LOT_SCAN.IGNORE.SUCCESS: {
      const touchedWeighLotScans = action.payload.weigh_lot_scans;
      const merged = bulkUpdateCases(
        state,
        { ...action, payload: touchedWeighLotScans },
        "byId",
        deserializeWeighLotScan,
      );

      // bulkUpdateCases spreads the previous byId entry (which may still carry a
      // `_previousStatus` snapshot from `applyOptimisticKeep`/`.Ignore`) before the deserialized
      // response, and the response never has a `_previousStatus` key to overwrite it with - so a
      // stale snapshot would otherwise survive the commit and corrupt a later rollback.
      const byId = { ...merged.byId };
      for (const { id } of touchedWeighLotScans) {
        if (byId[id] && "_previousStatus" in byId[id]) {
          const { _previousStatus, ...rest } = byId[id];
          byId[id] = rest;
        }
      }

      return { ...merged, byId };
    }
    default:
      return state;
  }
}

/**
 * Every non-deduplicated Weigh Lot Scan sharing `weighLotScan`'s EID, including itself - i.e. the
 * set the backend's keep decision considers together (see `KeepWeighLotScans._keep`). Everything
 * this needs (eid, weighLotId, status) already lives in this slice's own `byId`.
 */
function findKeepFamily(byId, weighLotScan) {
  return Object.values(byId).filter(
    other =>
      other.eid === weighLotScan.eid &&
      other.status !== WeighLotScanStatus.DEDUPLICATED,
  );
}

function isReweigh(family) {
  return uniq(family.map(member => member.weighLotId)).length > 1;
}

function snapshotAndSetStatus(byId, id, status) {
  const weighLotScan = byId[id];
  byId[id] = {
    ...weighLotScan,
    status,
    syncing: true,
    _previousStatus: weighLotScan._previousStatus ?? weighLotScan.status,
  };
}

/**
 * Optimistically predicts a keep decision: the resulting status (MAPPED vs REWEIGHED) depends
 * only on how many distinct Weigh Lots this EID spans, and marks every sibling sharing the EID
 * as IGNORED, the same as the backend will - all computable from this slice's own `byId`, so
 * there's nothing the backend knows here that the client doesn't already have.
 *
 * Siblings already `syncing` (claimed by another keep/ignore still in flight) are left alone
 * rather than re-decided, so two overlapping requests touching the same reweigh pair don't flip
 * each other's predictions back and forth before either resolves - whichever actually lands last
 * wins once SUCCESS/FAILURE settle it, same as it would server-side.
 */
function applyOptimisticKeep(state, weighLotScanIds) {
  const byId = { ...state.byId };
  for (const id of weighLotScanIds) {
    const weighLotScan = byId[id];
    if (!weighLotScan || weighLotScan.syncing) {
      continue;
    }

    const family = findKeepFamily(byId, weighLotScan);
    const keptStatus = isReweigh(family)
      ? WeighLotScanStatus.REWEIGHED
      : WeighLotScanStatus.MAPPED;

    snapshotAndSetStatus(byId, id, keptStatus);

    for (const sibling of family) {
      if (sibling.id !== id && !sibling.syncing) {
        snapshotAndSetStatus(byId, sibling.id, WeighLotScanStatus.IGNORED);
      }
    }
  }
  return { ...state, byId };
}

/**
 * Reverts everything `applyOptimisticKeep` could have touched for these ids - re-deriving the
 * same family (eids don't change, so this still finds the same members) rather than needing the
 * touched-ids list to have survived until FAILURE.
 */
function rollbackOptimisticKeep(state, weighLotScanIds) {
  const byId = { ...state.byId };
  const touchedIds = new Set();

  for (const id of weighLotScanIds) {
    const weighLotScan = byId[id];
    if (!weighLotScan) {
      continue;
    }
    touchedIds.add(id);
    for (const sibling of findKeepFamily(byId, weighLotScan)) {
      touchedIds.add(sibling.id);
    }
  }

  for (const id of touchedIds) {
    const weighLotScan = byId[id];
    if (weighLotScan && "_previousStatus" in weighLotScan) {
      const { _previousStatus, ...rest } = weighLotScan;
      byId[id] = { ...rest, status: _previousStatus, syncing: false };
    }
  }
  return { ...state, byId };
}

/**
 * Ignoring is much simpler than keeping - it only ever affects the exact requested ids, always
 * resolves to IGNORED, and doesn't touch any siblings - so it just needs a plain snapshot/restore.
 */
function applyOptimisticIgnore(state, weighLotScanIds) {
  const byId = { ...state.byId };
  for (const id of weighLotScanIds) {
    if (byId[id] && !byId[id].syncing) {
      snapshotAndSetStatus(byId, id, WeighLotScanStatus.IGNORED);
    }
  }
  return { ...state, byId };
}

function rollbackOptimisticIgnore(state, weighLotScanIds) {
  const byId = { ...state.byId };
  for (const id of weighLotScanIds) {
    const weighLotScan = byId[id];
    if (weighLotScan && "_previousStatus" in weighLotScan) {
      const { _previousStatus, ...rest } = weighLotScan;
      byId[id] = { ...rest, status: _previousStatus, syncing: false };
    }
  }
  return { ...state, byId };
}

function keepIgnoreRequestReducer(state, action) {
  switch (action.type) {
    case WEIGH_LOT_SCAN.KEEP.REQUEST:
      return applyOptimisticKeep(state, action.payload);
    case WEIGH_LOT_SCAN.IGNORE.REQUEST:
      return applyOptimisticIgnore(state, action.payload);
    case WEIGH_LOT_SCAN.KEEP.FAILURE:
      return rollbackOptimisticKeep(state, action.meta.weighLotScanIds);
    case WEIGH_LOT_SCAN.IGNORE.FAILURE:
      return rollbackOptimisticIgnore(state, action.meta.weighLotScanIds);
    default:
      return state;
  }
}

/**
 * Every other action type whose reducer case replaces a `byId` entry wholesale rather than
 * merging into it - and so drops the optimistic status, `syncing` flag and `_previousStatus`
 * snapshot an in-flight keep/ignore is relying on:
 *  - `apiModelOfflineFetchReducer`'s FETCH/FETCH_BULK/FETCH_SOME/FETCH_CHANGES successes
 *  - `apiModelOfflineUpdateReducer`'s UPDATE.SUCCESS/UPDATE.FROM_SOCKET (`onHandleAddScanToSale`,
 *    `onHandleManuallyMapToScan` and `onHandleAddWeightToSaleLot` in sagas/weighLotScans.js all
 *    dispatch a plain `WeighLotScanAction.update`, so this isn't hypothetical - nothing stops one
 *    of those landing, for the same id, while a bulk keep/ignore on it is still in flight)
 */
const wholesaleReplaceActionTypes = [
  WEIGH_LOT_SCAN.FETCH.SUCCESS,
  WEIGH_LOT_SCAN.FETCH_BULK.SUCCESS,
  WEIGH_LOT_SCAN.FETCH_SOME.SUCCESS,
  WEIGH_LOT_SCAN.FETCH_CHANGES.SUCCESS,
  WEIGH_LOT_SCAN.UPDATE.SUCCESS,
  WEIGH_LOT_SCAN.UPDATE.FROM_SOCKET,
];

/**
 * Re-applies the optimistic keep/ignore over any of the above landing while the request was
 * still in flight.
 *
 * Data that was queried (or arrived) before the keep/ignore request commits carries the status
 * from before the user's decision, so replacing the entry with it reads as the decision being
 * undone until `KEEP`/`IGNORE.SUCCESS` arrives and puts it back. Server-side `push_refresh` makes
 * a stale fetch overlapping routine rather than rare: the acting client is notified of its own
 * keep/ignore and fires a changes-since fetch off the back of it.
 *
 * The incoming status becomes the new `_previousStatus`, so a rollback restores what was last
 * said about it rather than a staler snapshot; fields other than the status are taken from the
 * incoming data, which is the freshest version of them we have.
 */
function retainInFlightKeepIgnore(previousState, nextState, action) {
  if (!wholesaleReplaceActionTypes.includes(action.type)) {
    return nextState;
  }

  const previousById = previousState?.byId || {};
  const inFlightIds = Object.keys(previousById).filter(
    id => "_previousStatus" in previousById[id],
  );

  if (!inFlightIds.length) {
    return nextState;
  }

  const byId = { ...nextState.byId };
  for (const id of inFlightIds) {
    const incoming = byId[id];
    // A changes-since fetch reporting the Weigh Lot Scan as deleted removes it entirely - an
    // in-flight decision on it is moot, so leave it gone rather than resurrecting it.
    if (!incoming) {
      continue;
    }
    // Same reference means this id's action didn't touch it - skip, or we'd snapshot
    // `_previousStatus` from its own optimistic status and turn a later rollback into a no-op.
    if (incoming === previousById[id]) {
      continue;
    }
    byId[id] = {
      ...incoming,
      status: previousById[id].status,
      syncing: true,
      _previousStatus: incoming.status,
    };
  }

  return { ...nextState, byId };
}

const composedWeighLotScanReducer = reduceReducers(
  create,
  comment,
  deleteReducer,
  fetch,
  update,
  bulkUpdateAndCreateReducer,
  keepIgnoreRequestReducer,
);

function weighLotScanReducer(state, action) {
  return retainInFlightKeepIgnore(
    state,
    composedWeighLotScanReducer(state, action),
    action,
  );
}

export default weighLotScanReducer;
